import {Box, Button, Popover, ScrollArea, Text, TextField} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import React, {useCallback, useEffect, useRef, useState} from "react";
import styles from "./styles/MessagingListRules.module.scss";
import WatsonApi from "../../../backends/WatsonApi";
import {MessagingListQueryOptions} from "./MessagingListRules";
import {IMessageListRuleTag} from "./MessagingListRuleQueryBuilder";
import {useStores} from "../../../stores/useStores";
import useTheme from "../../radix/useTheme";
import {useTranslation} from "react-i18next";
import i18n from "../../../internationalization/i18n";
import {IMessagingListModalIntent} from "./AddOrEditMessagingListModal";

export function RuleOrganizationSearch(props: {
    organization: IMessageListRuleTag | null,
    setOrganization: React.Dispatch<React.SetStateAction<IMessageListRuleTag | null>>
    messageIntent: "broadcast" | "message",
    modalIntent: IMessagingListModalIntent,
}) {
    const searchTimeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const {organizationStore} = useStores();
    const radixTheme = useTheme();
    const {t} = useTranslation('translation', {i18n});

    const [searchResultsLoading, setSearchResultsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<Pick<IMessageListRuleTag, "value" | "type" | "filter">[] | undefined>(undefined);
    const [searchResultsDropdownOpen, setSearchResultsDropdownOpen] = useState(false);

    let searchResultsContent;
    if (typeof searchResults !== "undefined") {
        if (searchResults.length === 0) {
            searchResultsContent = <Text>{t("Not Found? Refine your search please.")}</Text>
        } else {
            searchResultsContent = <>
                {searchResults.map(result => <Box
                    onClick={() => {
                        setSearchResultsDropdownOpen(false);
                        props.setOrganization(result);
                    }}
                    py={"1"}
                    className={styles.searchResultItem}
                >
                    {result.value}
                </Box>)}
            </>
        }
    }

    useEffect(() => {
        return () => {
            if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
        };
    }, []);

    const handleInputChange = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (searchTimeOutRef.current) clearTimeout(searchTimeOutRef.current);
            searchTimeOutRef.current = setTimeout(async () => {
                if (!e.target.value) return;
                setSearchResultsLoading(true);
                try {
                    const allowedTypes = ["school", "district", "classroom"];
                    if (!allowedTypes.includes(organizationStore.currentOrganization.type)) {
                        // make sure that the current org's type is included so can add current page to rules
                        allowedTypes.push(organizationStore.currentOrganization.type);
                        if (!allowedTypes.includes("section")) {
                            // if we're here it means this is a subpage, so make sure sections are included so sub-subpages are allowed
                            allowedTypes.push("section");
                        }
                    }

                    const client = await WatsonApi();
                    const response = await client.apis.organizations.organizations_list({
                        type__in: allowedTypes,
                        fields: ["title", "type", "id"],
                        search: e.target.value,
                        deleted: "false",
                        ordering: "title",
                        page_size: 20,
                        path__descendant_or_eq: organizationStore.currentOrganization.path,
                        with_role: props.modalIntent === "createMessage" ? true : undefined,
                    });
                    const results = JSON.parse(response.data).data;
                    const options = results.map((org) => ({
                        value: `${org.title} (${org.type})`,
                        type: MessagingListQueryOptions.Organization,
                        filter: org.id,
                    }));
                    setSearchResults(options);
                } catch (error) {
                    console.error("Error fetching organizations:", error);
                } finally {
                    setSearchResultsLoading(false);
                }
            }, 250);
        },
        [organizationStore.currentOrganization.path]
    );

    return <Popover.Root open={searchResultsDropdownOpen}>
        <Popover.Trigger onClick={() => setSearchResultsDropdownOpen(true)}>
            <Button style={{height: "initial", minHeight: "var(--base-button-height)"}}
                    variant={"outline"} color={props.organization ? "gray" : "red"}>
                {props.organization?.value || "Select"}
            </Button>
        </Popover.Trigger>
        <Popover.Content
            container={radixTheme.portalContainer?.current}
            onPointerDownOutside={() => setSearchResultsDropdownOpen(false)}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
        >
            <Box pb={"1"} className={"rt-SelectTrigger"}>
                <TextField.Root onChange={handleInputChange} placeholder={organizationStore.currentOrganization.title}>
                    <TextField.Slot side={"left"}>
                        {searchResultsLoading ? <FontAwesome name={"fa-solid fa-spinner"} spin/> :
                            <FontAwesome name={"fa-magnifying-glass"} prefix={"fas"}/>}
                    </TextField.Slot>
                </TextField.Root>
            </Box>
            <ScrollArea style={{maxHeight: "300px"}}>
                <Box mr={"3"}>
                    {searchResultsContent}
                </Box>
            </ScrollArea>
        </Popover.Content>
    </Popover.Root>
}
